/** biome-ignore-all lint/suspicious/noExplicitAny: my code my choice */
import { type ClassValue, clsx } from "clsx"
import { extendTailwindMerge } from "tailwind-merge"
import { colors, spacing } from "./collectedValues"

// Configure tailwind-merge with our design system's custom values
const twMerge = extendTailwindMerge({
  override: {
    theme: {
      colors,
      spacing,
      borderRadius: spacing, // borderRadius uses same scale as spacing
    },
  },
})

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

type AnyFunction = (...args: any[]) => any
export const noop: AnyFunction = () => {}

/**
 * Deeply merges multiple objects. Arrays are merged and deduplicated by value.
 */
export function deepMerge<T extends object>(...sources: (Partial<T> | undefined)[]): T {
  const isObject = (obj: any) => obj && typeof obj === "object" && !Array.isArray(obj)
  const mergeArrays = (a: any[], b: any[]) => Array.from(new Set([...(a || []), ...(b || [])]))

  const result = sources.reduce(
    (acc, obj) => {
      if (!obj) return acc
      Object.keys(obj).forEach((key) => {
        const accVal = (acc as any)[key]
        const objVal = (obj as any)[key]
        if (Array.isArray(accVal) && Array.isArray(objVal)) {
          ;(acc as any)[key] = mergeArrays(accVal, objVal)
        } else if (isObject(accVal) && isObject(objVal)) {
          ;(acc as any)[key] = deepMerge(accVal, objVal)
        } else if (Array.isArray(objVal)) {
          ;(acc as any)[key] = mergeArrays([], objVal)
        } else if (isObject(objVal)) {
          ;(acc as any)[key] = deepMerge({}, objVal)
        } else if (objVal !== undefined) {
          ;(acc as any)[key] = objVal
        }
      })
      return acc
    },
    {} as Partial<T>
  )
  return result as T
}
